<script setup lang="ts">
import type { ResponsibleFor } from '@/models/api-generated'
import { useId } from 'vue'

const props = defineProps<{
	responsibleFor?: ResponsibleFor
	title?: string
	type: string
}>()

const uid = useId()
</script>
<template>
	<h6 v-if="title">
		<i v-if="props.type === 'User'" class="bi-person-fill me-2 pie1"></i><i v-else class="bi-people-fill me-2"></i>{{ title }}
	</h6>
	<ul class="nav nav-tabs responsible-for" role="tablist" ref="tablist">
		<li v-if="props.responsibleFor?.assets?.length" :id="`responsible-assets-tab-${uid}`" class="nav-item" role="presentation">
			<button
				:data-bs-target="`#assets-${uid}`"
				:aria-controls="`assets-${uid}`"
				class="nav-link"
				data-bs-toggle="tab"
				type="button"
				role="tab"
			>
				{{ $t('assets') }} ({{ props.responsibleFor.assets.length }})
			</button>
		</li>

		<li v-if="props.responsibleFor?.dataAssets?.length" :id="`responsible-dataAssets-tab-${uid}`" class="nav-item" role="presentation">
			<button
				:data-bs-target="`#dataAssets-${uid}`"
				:aria-controls="`dataAssets-${uid}`"
				class="nav-link"
				data-bs-toggle="tab"
				type="button"
				role="tab"
			>
				{{ $t('dataAssets') }} ({{ props.responsibleFor.dataAssets.length }})
			</button>
		</li>

		<li v-if="props.responsibleFor?.methodMeta?.length" :id="`responsible-methodMeta-tab-${uid}`" class="nav-item" role="presentation">
			<button
				:data-bs-target="`#methodMeta-${uid}`"
				:aria-controls="`methodMeta-${uid}`"
				class="nav-link"
				data-bs-toggle="tab"
				type="button"
				role="tab"
			>
				{{ $t('measure', 2) }} ({{ props.responsibleFor.methodMeta.length }})
			</button>
		</li>

		<li v-if="props.responsibleFor?.moduleMeta?.length" :id="`responsible-moduleMeta-tab-${uid}`" class="nav-item" role="presentation">
			<button
				:data-bs-target="`#moduleMeta-${uid}`"
				:aria-controls="`moduleMeta-${uid}`"
				class="nav-link"
				data-bs-toggle="tab"
				type="button"
				role="tab"
			>
				{{ $t('module.label', 2) }} ({{ props.responsibleFor.moduleMeta.length }})
			</button>
		</li>

		<li v-if="props.responsibleFor?.moduleAssets?.length" :id="`responsible-moduleAssets-tab-${uid}`" class="nav-item" role="presentation">
			<button
				:data-bs-target="`#moduleAssets-${uid}`"
				:aria-controls="`moduleAssets-${uid}`"
				class="nav-link"
				data-bs-toggle="tab"
				type="button"
				role="tab"
			>
				{{ $t('assetAssociations') }} ({{ props.responsibleFor.moduleAssets.length }})
			</button>
		</li>
		<li
			v-if="props.responsibleFor?.businessProcesses?.length"
			:id="`responsible-businessProcesses-tab-${uid}`"
			class="nav-item"
			role="presentation"
		>
			<button
				:data-bs-target="`#businessProcesses-${uid}`"
				:aria-controls="`businessProcesses-${uid}`"
				class="nav-link"
				data-bs-toggle="tab"
				type="button"
				role="tab"
			>
				{{ $t('businessProcesses') }} ({{ props.responsibleFor.businessProcesses.length }})
			</button>
		</li>
		<li v-if="props.responsibleFor?.riskShort?.length" :id="`responsible-riskMeta-tab-${uid}`" class="nav-item" role="presentation">
			<button
				:data-bs-target="`#riskMeta-${uid}`"
				:aria-controls="`riskMeta-${uid}`"
				class="nav-link"
				data-bs-toggle="tab"
				type="button"
				role="tab"
			>
				{{ $t('risks', 2) }} ({{ props.responsibleFor.riskShort.length }})
			</button>
		</li>
	</ul>

	<div class="tab-content mt-4">
		<div v-if="props.responsibleFor?.assets?.length" :id="`assets-${uid}`" class="tab-pane" role="tabpanel">
			<div class="row">
				<div v-for="a in props.responsibleFor.assets" :key="a.id" class="col-md-6 text-truncate">
					{{ a.name }}
				</div>
			</div>
		</div>

		<div v-if="props.responsibleFor?.dataAssets?.length" :id="`dataAssets-${uid}`" class="tab-pane" role="tabpanel">
			<div v-for="a in props.responsibleFor.dataAssets" :key="a.id" class="col-md-6 text-truncate">
				{{ a.name }}
			</div>
		</div>

		<div v-if="props.responsibleFor?.methodMeta?.length" :id="`methodMeta-${uid}`" class="tab-pane" role="tabpanel">
			<div class="row">
				<div v-for="a in props.responsibleFor.methodMeta" :key="a.id" class="col-md-3">
					{{ a.method.name }}
				</div>
			</div>
		</div>
		<div v-if="props.responsibleFor?.moduleMeta?.length" :id="`moduleMeta-${uid}`" class="tab-pane" role="tabpanel">
			<div v-for="a in props.responsibleFor.moduleMeta" :key="a.id" class="col-md-3">
				{{ a.module.name }}
			</div>
		</div>
		<div v-if="props.responsibleFor?.moduleAssets?.length" :id="`moduleAssets-${uid}`" class="tab-pane" role="tabpanel">
			<div v-for="a in props.responsibleFor.moduleAssets" :key="a.id" class="col-md-6 text-truncate">
				{{ a.module.name }} - {{ a.asset.name }}
			</div>
		</div>
		<div v-if="props.responsibleFor?.businessProcesses?.length" :id="`businessProcesses-${uid}`" class="tab-pane" role="tabpanel">
			<div v-for="a in props.responsibleFor.businessProcesses" :key="a.id" class="col-md-6 text-truncate">
				{{ a.name }}
			</div>
		</div>
		<div v-if="props.responsibleFor?.riskShort?.length" :id="`riskMeta-${uid}`" class="tab-pane" role="tabpanel">
			<div v-for="a in props.responsibleFor.riskShort" :key="a.metaId" class="col-md-6 text-truncate">
				{{ a.name }}
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.tab-content
  max-height: 50vh
  overflow-x: hidden
  overflow-y: auto
</style>
